"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { trainingTours } from "@/config/training-tours";
import { TOUR_COMPLETED_EVENT, useTour } from "@/contexts/tour-provider";
import { toast } from "@/lib/use-toast";
import type { TrainingResponse } from "@/types/training";
import { trainingQueryKeys, useCompleteTrainingTour, useTraining } from "@/api/provider/training/training-query";
import { useCalendarStore } from "@/stores/calendar-store";
import { useTrainingStore } from "@/stores/training-store";

/** Lets the screen settle before its tour starts. */
const TOUR_START_DELAY_MS = 1000;
/** How often, and for how long, to look for the first step's target before starting a tour. */
const TOUR_TARGET_POLL_MS = 200;
const TOUR_TARGET_TIMEOUT_MS = 15_000;
/** Lets a burst of saves (a checkout, a note) settle before checking for finished tasks. */
const RECHECK_DELAY_MS = 1500;
function pathAfterTenant(pathname: string): string {
  const parts = pathname.split("/");
  return parts.length > 2 ? `/${parts.slice(2).join("/")}` : "/";
}

/**
 * Runs the parts of training that live outside its own screens: starting a task's tour
 * once its screen opens, ticking off tour tasks, and checking for finished tasks after
 * the person saves something anywhere in the app.
 */
export function TrainingWatcher() {
  const $ = _c(1);
  const {
    data
  } = useTraining();
  const hasPlan = !!data?.available && !!data.plan;
  if (!hasPlan) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ActiveTrainingWatcher />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function ActiveTrainingWatcher() {
  usePendingTour();
  useTourCompletion();
  useRecheckAfterSaves();
  return null;
}
function usePendingTour() {
  const $ = _c(5);
  const pathname = usePathname();
  const pendingTour = useTrainingStore(_temp);
  const {
    startTour
  } = useTour();
  let t0;
  let t1;
  if ($[0] !== pathname || $[1] !== pendingTour || $[2] !== startTour) {
    t0 = () => {
      if (!pendingTour) {
        return;
      }
      if (pathAfterTenant(pathname) !== pendingTour.path) {
        return;
      }
      if (pendingTour.path === "/check-in/calendar") {
        useCalendarStore.getState().setIsCalendarSidebarCollapsed(false);
      }
      const firstSelector = trainingTours.find(t => t.tour === pendingTour.tour)?.steps[0]?.selector;
      const startedAt = Date.now();
      const timer = setInterval(() => {
        const elapsed = Date.now() - startedAt;
        if (elapsed < TOUR_START_DELAY_MS) {
          return;
        }
        const ready = !firstSelector || document.querySelector(firstSelector) !== null;
        if (!ready && elapsed < TOUR_TARGET_TIMEOUT_MS) {
          return;
        }
        clearInterval(timer);
        useTrainingStore.getState().clearPendingTour();
        if (ready) {
          startTour(pendingTour.tour);
        }
      }, TOUR_TARGET_POLL_MS);
      return () => clearInterval(timer);
    };
    t1 = [pathname, pendingTour, startTour];
    $[0] = pathname;
    $[1] = pendingTour;
    $[2] = startTour;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
}
function _temp(s) {
  return s.pendingTour;
}
function useTourCompletion() {
  const {
    data
  } = useTraining();
  const completeTour = useCompleteTrainingTour();
  const latest = useRef(data);
  latest.current = data;
  useEffect(() => {
    const onCompleted = (event: Event) => {
      const tour = (event as CustomEvent<string>).detail;
      const open = latest.current?.items.some(item => item.status === "OPEN" && item.task.completion === "TOUR" && item.task.tour === tour);
      if (open) completeTour.mutate(tour);
    };
    window.addEventListener(TOUR_COMPLETED_EVENT, onCompleted);
    return () => window.removeEventListener(TOUR_COMPLETED_EVENT, onCompleted);
  }, [completeTour]);
}
function doneKeys(data: TrainingResponse | undefined): Set<string> {
  return new Set((data?.items ?? []).filter(item => item.status === "DONE").map(item => item.itemKey));
}
function useRecheckAfterSaves() {
  const $ = _c(7);
  const queryClient = useQueryClient();
  const {
    data
  } = useTraining();
  const previousDone = useRef(null);
  let t0;
  let t1;
  if ($[0] !== data || $[1] !== queryClient) {
    t0 = () => {
      if (!data?.plan) {
        return;
      }
      const now = doneKeys(data);
      const before = previousDone.current;
      previousDone.current = now;
      if (!before) {
        return;
      }
      const finished = data.items.filter(item => now.has(item.itemKey) && !before.has(item.itemKey));
      if (finished.length === 0) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: trainingQueryKeys.team()
      });
      const total = data.items.length;
      const settled = data.totals.done + data.totals.skipped;
      for (const item_0 of finished) {
        toast({
          title: `Training task done: ${item_0.task.title}`,
          description: `${settled} of ${total} done`,
          variant: "success"
        });
      }
    };
    t1 = [data, queryClient];
    $[0] = data;
    $[1] = queryClient;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] !== queryClient) {
    t2 = () => {
      let timer = null;
      const unsubscribe = queryClient.getMutationCache().subscribe(event => {
        if (event.type !== "updated") {
          return;
        }
        const mutation = event.mutation;
        if (mutation.state.status !== "success") {
          return;
        }
        if (mutation.options.mutationKey?.[0] === trainingQueryKeys.all[0]) {
          return;
        }
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: trainingQueryKeys.mine()
          });
        }, RECHECK_DELAY_MS);
      });
      return () => {
        unsubscribe();
        if (timer) {
          clearTimeout(timer);
        }
      };
    };
    t3 = [queryClient];
    $[4] = queryClient;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
}